import { useState, useEffect, useRef } from "react";
import styles from "./Style.module.css";
import BorderedButton from "../Buttons/BorderedButton";
import PaymentMethodsLoader from "./PaymentMethodsLoader";
import { getPaymentMethods } from "../../APICalling/APICalling";
import { useGlobalContext } from "../../Context/GlobalContext";
import { ERROR_MESSAGES } from "../../Utils/Constants/errorMessages";

/** Normalizes getPaymentMethods result (flat body, nested data, or axios error from APICalling). */
const parsePaymentMethodsResponse = (response) => {
	if (!response || typeof response !== "object") {
		return { ok: false, list: [], message: "" };
	}
	if (response.response?.data && typeof response.response.data === "object") {
		const d = response.response.data;
		return {
			ok: d.success === true,
			list: Array.isArray(d.data) ? d.data : [],
			message: d.success ? "" : (d.message || "").trim(),
		};
	}
	const d = response.success !== undefined ? response : response.data;
	if (d && typeof d === "object" && "success" in d) {
		return {
			ok: d.success === true,
			list: Array.isArray(d.data) ? d.data : [],
			message: d.success ? "" : (d.message || "").trim(),
		};
	}
	return { ok: false, list: [], message: "" };
};

const PaymentMethods = ({
	amountDue,
	isProcessingPayment,
	setIsProcessingPayment,
	enteredChargeAmount,
	parsedEnteredCharge,
	hasValidEnteredAmount,
	setAmountInputError,
	validateEnteredAmount,
	onNext,
	isMembershipPayment = false,
	paymentMethodsRefreshKey = 0,
	setInsufficientCreditMessage,
	hasOpenMembershipSale = false,
	onOpenMembershipSale,
}) => {
	const {
		selectedOutlet,
		loggedOutletID,
		customerCreditDetails,
		paymentSummary,
		setPaymentSummary,
		setAmountDue,
		setCurrentPaymentMethod,
		setCurrentPaymentMethodId,
		setCurrentPaymentMethodTitle,
		setAmountToCharge,
	} = useGlobalContext();

	const outletId = selectedOutlet || loggedOutletID;

	const [paymentMethodsList, setPaymentMethodsList] = useState([]);
	const [paymentMethodsErrorMessage, setPaymentMethodsErrorMessage] =
		useState("");
	const [paymentMethodsFetched, setPaymentMethodsFetched] = useState(false);


	useEffect(() => {
		const fetchPaymentMethods = async () => {
			if (!outletId) {
				setPaymentMethodsList([]);
				setPaymentMethodsErrorMessage("");
				setPaymentMethodsFetched(false);
				return;
			}
			setPaymentMethodsFetched(false);
			setPaymentMethodsErrorMessage("");
			try {
				const queryParams = isMembershipPayment
					? { module: "membership", }
					: { module: "booking" };
				const response = await getPaymentMethods(outletId, queryParams);
				const { ok, list, message } = parsePaymentMethodsResponse(response);
				if (ok) {
					setPaymentMethodsList(list);
					setPaymentMethodsErrorMessage("");
				} else {
					setPaymentMethodsList([]);
					setPaymentMethodsErrorMessage(
						message || "Unable to load payment methods."
					);
				}
			} catch (error) {
				console.error("Error fetching payment methods:", error);
				setPaymentMethodsList([]);
				setPaymentMethodsErrorMessage("Unable to load payment methods.");
			} finally {
				setPaymentMethodsFetched(true);
			}
		};
		fetchPaymentMethods();
	}, [outletId, isMembershipPayment, paymentMethodsRefreshKey]);

	useEffect(() => {
		setInsufficientCreditMessage?.("");
	}, [enteredChargeAmount, customerCreditDetails, setInsufficientCreditMessage]);


	if (!(amountDue > 0)) {
		return null;
	}

	if (!paymentMethodsFetched && outletId) {
		return <PaymentMethodsLoader />;
	}

	return (
		<div className={styles.paymentMethodsContainer}>
			<div className={styles.paymentGrid}>
				{paymentMethodsErrorMessage ? (
					<p
						className={styles.errorMessage}
						style={{ gridColumn: "1 / -1", paddingLeft: 0, marginTop: 0 }}>
						{paymentMethodsErrorMessage}
					</p>
				) : paymentMethodsFetched &&
				outletId &&
				paymentMethodsList.length === 0 ? (
					<p
						className={styles.errorMessage}
						style={{ gridColumn: "1 / -1", paddingLeft: 0, marginTop: 0 }}>
						No payment method found!
					</p>
				) : (
					paymentMethodsList.map((pm) => {
						const displayTitle = (pm?.title || "").trim();
						const methodTitle = displayTitle.toUpperCase();
						const slug = pm?.slug || "";
						const methodKey = slug.toLowerCase();
						const noCustomerCredit = !customerCreditDetails || customerCreditDetails.length === 0;
						const isCreditDependentMethod = methodKey === "cashless" || methodKey === "membership_credit";
						const currentCreditBalance = parseFloat(customerCreditDetails?.[0]?.current_credit_balance);

						const chargeExceedsCreditBalance =
							Number.isFinite(parsedEnteredCharge) &&
							Number.isFinite(currentCreditBalance) &&
							parsedEnteredCharge > currentCreditBalance + 0.001;
						const isCreditMethodDisabledDueToAmount =
							chargeExceedsCreditBalance && isCreditDependentMethod;
						const hardDisabled =
							isProcessingPayment ||
							!hasValidEnteredAmount ||
							(noCustomerCredit && isCreditDependentMethod);
						return (
							<BorderedButton
								key={pm?.id}
								text={methodTitle}
								disabled={hardDisabled}
								tooltip={isCreditMethodDisabledDueToAmount ? ERROR_MESSAGES.INSUFFICIENT_CASHLESS_BALANCE : ""}
								onClick={() => {
									if (
										isCreditMethodDisabledDueToAmount &&
										!hardDisabled
									) {
										setInsufficientCreditMessage?.(ERROR_MESSAGES.INSUFFICIENT_CASHLESS_BALANCE);
										return;
									}
									if (isProcessingPayment) return;
									if (hasOpenMembershipSale && isCreditDependentMethod) {
										onOpenMembershipSale?.();
										return;
									}
									if (noCustomerCredit && isCreditDependentMethod) return;
									if (isCreditMethodDisabledDueToAmount) return;
									const validationError = validateEnteredAmount(enteredChargeAmount);
									if (validationError) {
										setAmountInputError(validationError);
										return;
									}
									let chargeCap = parsedEnteredCharge;
									if (methodKey === "membership_credit") {
										const gstRemoval = parseFloat(paymentSummary?.gst) || 0;
										const subtotal = Number(paymentSummary?.subtotal) || 0;
										setPaymentSummary((prev) => ({
											...prev,
											gst: 0,
											midtotal: subtotal,
											total: subtotal.toFixed(2),
										}));
										const parsedDue = parseFloat(amountDue);
										const baseDue = isNaN(parsedDue) ? 0 : parsedDue;
										const nextDue = Math.max(0, baseDue - gstRemoval);
										setAmountDue(nextDue.toFixed(2));
										chargeCap = Math.min(parsedEnteredCharge, nextDue);
									}
									setAmountToCharge(chargeCap.toFixed(2));
									setIsProcessingPayment(true);
									setCurrentPaymentMethod(methodKey);
									setCurrentPaymentMethodId(pm?.id);
									setCurrentPaymentMethodTitle(displayTitle);
									onNext();
								}}
								className={`${styles.defaultBtn}`}
							/>
						);
					})
				)}
			</div>
		</div>
	);
};

export default PaymentMethods;
